/* global
  copyTextProductCard,
  createLoadMoreButton,
  waterdropAppApi
*/

/**
 * <rewards-vips> Web Component
 *
 * 管理已登录客户的 VIP 奖励页面。通过 waterdropAppApi 代理与
 * Smile.io 忠诚度 API 及自定义抽奖记录 API 通信，展示：
 *   1. 积分余额 & VIP 等级进度
 *   2. 积分活动弹窗 (Points Activities) — 分页的积分交易记录
 *   3. 我的奖励弹窗 (My Rewards)        — 两个标签页：
 *        "已兑换" (Smile reward_fulfillments)
 *        "抽奖奖品" (prize-draw records)
 *
 * 必需属性：data-logged-in, data-customer-email
 */
class RewardsVips extends HTMLElement {
  constructor() {
    super()
    this.customer = null
    this.currentTierIdx = -1
    this.splide = null

    /*
     * "我的奖励"弹窗状态。
     * 每个标签页独立维护 loaded/loading 标记、数据列表和分页信息，
     * 以支持首次切换时才懒加载数据。
     */
    this._mrActiveTab = 'redeemed'
    this._mrTabs = {
      redeemed: { loaded: false, loading: false, items: [], nextCursor: null },
      spins: { loaded: false, loading: false, items: [], hasMore: false, page: 1 }
    }
    this._mrEventsbound = false
  }

  get isLoggedIn() {
    return this.hasAttribute('data-logged-in')
  }

  get email() {
    return this.dataset.customerEmail || ''
  }

  connectedCallback() {
    this.load()
  }

  async load() {
    if (!this.isLoggedIn) return

    try {
      const smileCustomer = await waterdropAppApi({
        proxy: 'rewards/smile',
        endpoint: '/customers',
        query: { include: 'vip_status', email: this.email }
      })

      this.customer = smileCustomer?.customers?.[0]
      this.updateUser()
      this._initPointsActivities()
      this.bindMyRewardsOpener()
    } catch (e) {
      console.error('[RewardsVips] load', e)
      window.showMessage?.('Failed to load data. Please refresh and try again.', 'error')
    }
  }

  updateUser() {
    if (!this.customer) return
    this.querySelectorAll('[data-tier-progress]').forEach(el => {
      el.textContent = `€${Math.ceil(this.customer.vip_status.delta_to_next_vip_tier).toLocaleString()}`
    })

    /*
     * VIP 等级进度条：等级区间由元素上的 data-tier-start / data-tier-end 属性定义。
     * delta_to_next_vip_tier 表示距下一等级的剩余消费额，
     * 进度 = (已消费 / 总区间) 并限制在 [0%, 100%]
     */
    const progressEl = this.querySelector('[data-display-progress]')
    if (progressEl) {
      const tierStart = Number(progressEl.dataset.tierStart)
      const tierEnd = Number(progressEl.dataset.tierEnd)
      const tierSpan = tierEnd - tierStart
      const progressValue = ((tierSpan - this.customer.vip_status.delta_to_next_vip_tier) / tierSpan) * 100
      const progressPercent = Math.max(0, Math.min(100, progressValue))
      progressEl.style.width = `${progressPercent}%`
    }
  }

  /* ── Points Activities ── */
  _initPointsActivities() {
    const paModal = document.querySelector('#PointsActivitiesModal points-activities-modal')
    if (paModal) paModal.customerId = this.customer.id
  }

  /* ── My Rewards ── */

  bindMyRewardsOpener() {
    this.querySelectorAll("modal-opener[data-modal='MyRewardsModal']").forEach(opener => {
      opener.addEventListener('click', () => this.openMyRewards())
    })
  }

  /**
   * 每次打开"我的奖励"弹窗时调用。
   * 重置所有标签页状态并重新拉取数据，确保用户看到最新内容。
   * 事件委托仅绑定一次（由 _mrEventsbound 守卫）。
   */
  async openMyRewards() {
    if (!this._mrEventsbound) {
      this.bindMyRewardsEvents()
      this._mrEventsbound = true
    }
    this._mrActiveTab = 'redeemed'
    const modal = document.getElementById('MyRewardsModal')
    if (modal) {
      modal.querySelectorAll('[data-mr-tab]').forEach(t => {
        t.classList.toggle('mr-tab--active', t.dataset.mrTab === 'redeemed')
      })
      modal.querySelectorAll('[data-mr-panel]').forEach(panel => {
        panel.style.display = panel.dataset.mrPanel === 'redeemed' ? '' : 'none'
      })
    }
    Object.keys(this._mrTabs).forEach(tab => {
      const state = this._mrTabs[tab]
      state.loaded = false
      state.items = []
      if (tab === 'redeemed') state.nextCursor = null
      if (tab === 'spins') {
        state.hasMore = false
        state.page = 1
      }
      this._resetTabSkeleton(tab)
    })
    this._loadTabIfNeeded(this._mrActiveTab)
  }

  /**
   * 懒加载标签页数据。若数据已加载或正在请求中则跳过。
   * 传入 cursor/page 时追加数据（加载更多）；否则替换。
   *
   * "redeemed" → Smile /reward_fulfillments（游标分页）
   * "spins"    → 自定义 /customer/draw_records（页码分页，按折扣码/礼品卡类型过滤）
   */
  async _loadTabIfNeeded(tab, cursor = null) {
    const state = this._mrTabs[tab]
    const isLoadMore = !!cursor
    if (!isLoadMore && (state.loaded || state.loading)) return
    if (isLoadMore && state.loading) return
    state.loading = true

    try {
      if (tab === 'redeemed') {
        const params = { customer_id: this.customer.id }
        if (cursor) params.cursor = cursor
        const fulfillmentsRes = await waterdropAppApi({
          proxy: 'rewards/smile',
          endpoint: '/reward_fulfillments',
          query: params
        })
        const newItems = this._normalizeRedeemed(fulfillmentsRes?.reward_fulfillments || [])
        state.items = isLoadMore ? state.items.concat(newItems) : newItems
        state.nextCursor = fulfillmentsRes?.metadata?.next_cursor || null
      } else if (tab === 'spins') {
        const page = isLoadMore ? state.page + 1 : 1
        const res = await waterdropAppApi({
          proxy: 'rewards/api',
          endpoint: '/customer/draw_records',
          query: { prize_type: 'PRODUCT_DISCOUNT_CODE,ORDER_DISCOUNT_CODE,GIFT_CARD,CUSTOM', limit: 50, page }
        })
        const drawRecords = res?.data?.list || []
        const newItems = this._normalizeSpins(drawRecords)
        state.items = isLoadMore ? state.items.concat(newItems) : newItems
        state.hasMore = !!res?.data?.has_more
        state.page = page
      }
      state.loaded = true
    } catch (e) {
      console.error(`[RewardsVips] loadTab:${tab}`, e)
      if (!isLoadMore) state.items = []
      if (tab === 'redeemed') state.nextCursor = null
      window.showMessage?.('Failed to load data. Please refresh and try again.', 'error')
    } finally {
      state.loading = false
    }

    this._renderTabCards(tab)
  }

  _switchTab(tab) {
    if (tab === this._mrActiveTab) return
    this._mrActiveTab = tab

    const modal = document.getElementById('MyRewardsModal')
    if (!modal) return

    modal.querySelectorAll('[data-mr-tab]').forEach(t => {
      t.classList.toggle('mr-tab--active', t.dataset.mrTab === tab)
    })
    modal.querySelectorAll('[data-mr-panel]').forEach(panel => {
      panel.style.display = panel.dataset.mrPanel === tab ? '' : 'none'
    })

    this._loadTabIfNeeded(tab)
  }

  forceReloadTab(tab) {
    const state = this._mrTabs[tab]
    state.loaded = false
    state.loading = false
    state.items = []
    if (tab === 'redeemed') state.nextCursor = null
    if (tab === 'spins') {
      state.hasMore = false
      state.page = 1
    }
    this._resetTabSkeleton(tab)
    this._loadTabIfNeeded(tab)
  }

  async handleLoadMoreRedeemed() {
    const state = this._mrTabs.redeemed
    if (state.loading || !state.nextCursor) return
    await this._loadTabIfNeeded('redeemed', state.nextCursor)
  }

  async handleLoadMoreSpins() {
    const state = this._mrTabs.spins
    if (state.loading || !state.hasMore) return
    await this._loadTabIfNeeded('spins', true)
  }

  _resetTabSkeleton(tab) {
    const container = document.querySelector(`[data-mr-cards="${tab}"]`)
    if (!container) return
    const panel = container.closest('[data-mr-panel]')
    if (panel?.querySelector('.mr-card--skeleton')) return
    const tpl = document.querySelector('[data-mr-skeleton-template]')
    if (!tpl) return
    const fragment = document.createDocumentFragment()
    for (let i = 0; i < 4; i++) {
      fragment.appendChild(tpl.content.cloneNode(true))
    }
    container.innerHTML = ''
    container.appendChild(fragment)
  }

  /*
   * ── 标准化奖励数据 ──
   * 两个标准化方法将不同 API 的返回结构映射为统一的卡片数据模型，
   * 使 _renderRewardCard 可以统一处理。
   * 排序规则：有效（未使用）的奖励优先，其次按创建时间倒序。
   */

  _normalizeRedeemed(fulfillments) {
    return fulfillments
      .map(r => {
        const isExpired = r.expires_at && new Date(r.expires_at) < new Date()
        const isActive = r.usage_status === 'unused' && !isExpired
        let stampText = null
        if (isExpired) stampText = 'EXPIRED'
        else if (r.usage_status === 'used') stampText = 'USED'

        const createdDate = this._formatDateShort(r.created_at)
        const expiresDate = r.expires_at ? this._formatDateShort(r.expires_at) : null
        const datesText = expiresDate ? `${createdDate}-${expiresDate}` : 'No time limit'

        return {
          id: `r-${r.id}`,
          source: 'redeemed',
          name: r.name,
          imageUrl: r.image_url || null,
          code: r.code || null,
          datesText,
          rules: r.terms_and_conditions || null,
          isActive,
          colorTheme: isActive ? 'orange' : 'grey',
          stampText: isActive ? null : stampText,
          createdAt: new Date(r.created_at).getTime(),
          ctaUrl: null
        }
      })
      .sort((a, b) => {
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1
        return b.createdAt - a.createdAt
      })
  }

  /** 礼品卡使用"蓝色"主题，折扣码使用"橙色"主题。 */
  _normalizeSpins(drawRecords) {
    return drawRecords
      .map(r => {
        const isActive = r.discount_status === 'ACTIVE' || r.discount_status === 'PENDING'
        const isGiftCard = r.prize_type === 'GIFT_CARD'
        const ctaUrl = typeof r.winning_pop_up_cta_url === 'string' ? r.winning_pop_up_cta_url.trim() : ''
        const createdDate = r.discount_code_start
          ? this._formatDateShort(r.discount_code_start)
          : this._formatDateShort(r.draw_time)
        const expiresDate = r.discount_code_expiry ? this._formatDateShort(r.discount_code_expiry) : null
        const datesText = expiresDate ? `${createdDate}-${expiresDate}` : 'No time limit'
        const stampText = r.discount_status === 'ACTIVE' || r.discount_status === 'PENDING' ? null : r.discount_status

        return {
          id: `s-${r.draw_id}`,
          source: 'spin',
          name: r.prize_name,
          imageUrl: r.prize_image || null,
          code: r.discount_code || null,
          datesText,
          rules: r.winning_pop_up_rule || null,
          isActive,
          colorTheme: isActive ? (isGiftCard ? 'blue' : 'orange') : 'grey',
          stampText,
          createdAt: new Date(r.draw_time).getTime(),
          ctaUrl: isActive && !!ctaUrl ? ctaUrl : null
        }
      })
      .sort((a, b) => {
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1
        return b.createdAt - a.createdAt
      })
  }

  /** 富文本去掉标签后是否还有可见文字。空段落如 <p><br></p> 视为无内容。 */
  hasRichTextContent(html) {
    if (typeof html !== 'string') return false
    const text = html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;|&#160;|&#xa0;/gi, ' ')
      .replace(/\u00a0/g, ' ')
      .trim()
    return text.length > 0
  }

  _formatDateShort(dateStr) {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
  }

  /* ── 渲染标签页卡片 ──
   * 使用 Liquid 文件中定义的 <template> 元素（[data-mr-card-template]）
   * 克隆生成奖励卡片，若还有更多分页则追加"查看更多"按钮。
   */

  _renderTabCards(tab) {
    const container = document.querySelector(`[data-mr-cards="${tab}"]`)
    if (!container) return

    const panel = container.closest('[data-mr-panel]')
    panel?.querySelector('.mr-load-more')?.remove()

    const { items } = this._mrTabs[tab]
    const cardTemplate = document.querySelector('[data-mr-card-template]')

    if (!items.length) {
      container.innerHTML = '<p class="mr-empty p1-s">No rewards yet</p>'
      return
    }

    container.innerHTML = ''
    const fragment = document.createDocumentFragment()
    items.forEach(item => {
      const card = this._renderRewardCard(item, cardTemplate)
      if (card) fragment.appendChild(card)
    })
    container.appendChild(fragment)

    if (tab === 'redeemed' && this._mrTabs.redeemed.nextCursor && panel) {
      createLoadMoreButton({
        containerEl: panel,
        wrapClass: 'mr-load-more',
        onClick: () => this.handleLoadMoreRedeemed()
      })
    }

    if (tab === 'spins' && this._mrTabs.spins.hasMore && panel) {
      createLoadMoreButton({
        containerEl: panel,
        wrapClass: 'mr-load-more',
        variant: 'text',
        onClick: () => this.handleLoadMoreSpins()
      })
    }
  }

  /**
   * 克隆卡片模板并填充标准化后的奖励数据。
   * 没有对应数据的元素（如无折扣码、无规则、无徽章）会从克隆节点中移除，
   * 以保持 DOM 整洁。
   */
  _renderRewardCard(item, cardTemplate) {
    const card = cardTemplate.content.firstElementChild.cloneNode(true)
    card.classList.add(`mr-card--${item.colorTheme}`)

    const imageEl = card.querySelector('[data-mr-image]')
    if (item.imageUrl && imageEl) {
      imageEl.src = item.imageUrl
      imageEl.alt = item.name || ''
    }

    const nameEl = card.querySelector('[data-mr-name]')
    if (nameEl) nameEl.textContent = item.name || ''

    const datesEl = card.querySelector('[data-mr-dates]')
    if (datesEl) datesEl.textContent = `Valid time: ${item.datesText || ''}`

    const rulesBtn = card.querySelector('[data-mr-show-rules]')
    if (rulesBtn && this.hasRichTextContent(item.rules)) {
      rulesBtn.dataset.mrShowRules = encodeURIComponent(item.rules)
    } else {
      rulesBtn?.remove()
    }

    const bottomEl = card.querySelector('[data-mr-bottom]')
    if (item.code && bottomEl) {
      const copyCodeBtn = card.querySelector('.mr-card-code')
      if (copyCodeBtn) {
        copyCodeBtn.dataset.value = item.code
        const codeTextEl = copyCodeBtn.querySelector('p')
        if (codeTextEl) codeTextEl.textContent = item.code
      }

      const useBtn = card.querySelector('.mr-card-use-btn')
      if (item.ctaUrl && useBtn) {
        useBtn.setAttribute('href', item.ctaUrl)
        useBtn.setAttribute('data-mr-copy', item.code)
        useBtn.setAttribute('aria-label', 'Copy and use discount code and go to the page')
      } else {
        useBtn?.remove()
      }
    } else {
      bottomEl?.remove()
    }

    const stampEl = card.querySelector('[data-mr-badge]')
    if (item.stampText && stampEl) {
      const stampTextEl = card.querySelector('[data-mr-badge-text]')
      if (stampTextEl) stampTextEl.textContent = item.stampText
    } else {
      stampEl?.remove()
    }

    return card
  }

  /*
   * ── "我的奖励"事件处理（事件委托，仅绑定一次） ──
   * 在弹窗上挂载单一 click 监听器，通过事件委托处理所有交互元素，
   * 避免为每张卡片单独绑定事件（重新渲染时无需清理）。
   *
   * 处理的交互：
   *   1. 标签页切换       — [data-mr-tab]
   *   2. "使用"按钮       — 先复制折扣码到剪贴板，再跳转到 CTA 链接
   *   3. 查看规则         — 打开规则浮层并显示解码后的文本
   *   4. 关闭规则         — 关闭按钮或点击浮层遮罩
   */
  bindMyRewardsEvents() {
    const modal = document.getElementById('MyRewardsModal')
    if (!modal) return

    modal.addEventListener('click', e => {
      const tab = e.target.closest('[data-mr-tab]')
      if (tab) {
        this._switchTab(tab.dataset.mrTab)
        return
      }

      /* "使用"按钮：先复制折扣码，无论复制成功或失败都跳转 */
      const useBtn = e.target.closest('.mr-card-use-btn')
      if (useBtn) {
        e.preventDefault()
        const code = useBtn.dataset.mrCopy
        const ctaUrl = useBtn.getAttribute('href')
        if (!ctaUrl) return
        if (!code) {
          window.location.assign(ctaUrl)
          return
        }
        copyTextProductCard(code)
          .then(() => {
            window.location.assign(ctaUrl)
          })
          .catch(err => {
            console.error('Copy failed:', err)
            window.location.assign(ctaUrl)
          })
        return
      }

      /* 规则文本通过 URI 编码存储在 data 属性中，以避免 HTML 转义问题 */
      const rulesBtn = e.target.closest('[data-mr-show-rules]')
      if (rulesBtn) {
        const rulesText = decodeURIComponent(rulesBtn.dataset.mrShowRules)
        const overlay = modal.querySelector('[data-mr-rules-overlay]')
        const textEl = modal.querySelector('[data-mr-rules-text]')
        if (overlay && textEl) {
          textEl.innerHTML = rulesText
          overlay.classList.add('mr-rules-overlay--open')
        }
        return
      }

      const rulesClose = e.target.closest('[data-mr-rules-close]')
      if (rulesClose) {
        modal.querySelector('[data-mr-rules-overlay]')?.classList.remove('mr-rules-overlay--open')
        return
      }

      if (e.target.matches('[data-mr-rules-overlay]')) {
        e.target.classList.remove('mr-rules-overlay--open')
      }
    })
  }
}

customElements.define('rewards-vips', RewardsVips)
